import { Autocomplete, Box, Chip, FormControl, FormHelperText, TextField } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import { CustomMultiSelectChipProps } from '../../interfaceTypes/common'
import { MENU_ITEM_HEIGHT } from '../../constants'
import React, { useState, useEffect, useRef } from 'react'

const CustomMultiSelectChip = ({
  label,
  name,
  chipLabel,
  getLabel,
  setPage,
  setSearchTerm,
  disabled = false,
  required = false,
  noOptionsText,
  children,
  handleChange
}: CustomMultiSelectChipProps) => {
  const {
    control,
    formState: { errors },
    watch
  } = useFormContext()
  const [inputValue, setInputValue] = useState('')
  const previousSelectedRef = useRef<string[]>([])

  const fieldValue = watch(name)
  const error = errors[name] as { message: string }

  // Reset input value when field is cleared/reset
  useEffect(() => {
    if (!fieldValue || fieldValue.length === 0) {
      setInputValue('')
      setSearchTerm?.('')
    }
  }, [fieldValue, setSearchTerm])

  return (
    <FormControl variant="outlined" sx={{ marginTop: 1.5, marginBottom: 1 }} error={Boolean(error)} fullWidth>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const options = React.Children.toArray(children)
            .filter(React.isValidElement)
            .map((child: any) => ({
              value: child.props.value,
              label: getLabel(child.props.value)
            }))

          const selectedValues = (field.value || []).map((val: string) => ({
            value: val,
            label: getLabel(val)
          }))

          return (
            <Autocomplete
              multiple
              fullWidth
              options={options}
              value={selectedValues}
              disabled={disabled}
              inputValue={inputValue}
              noOptionsText={noOptionsText}
              onInputChange={(_, newInputValue, reason) => {
                // Only update input value if it's from typing, not from selection
                if (reason === 'input') {
                  setInputValue(newInputValue)
                  setSearchTerm?.(newInputValue)
                }
              }}
              onChange={(event, newValue) => {
                const previousSelected = previousSelectedRef.current
                const newSelectedIds = newValue.map((item: any) => item.value)

                handleChange?.(newSelectedIds, previousSelected)
                previousSelectedRef.current = newSelectedIds
                field.onChange(newSelectedIds)

                // Clear the input value on selection
                setInputValue('')
                setSearchTerm?.('')
              }}
              getOptionLabel={option => option.label}
              renderTags={(selected, getTagProps) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((item, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={item.value}
                      label={chipLabel(item.value)}
                      onMouseDown={event => event.stopPropagation()}
                      sx={{
                        '.MuiChip-label': {
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start'
                        }
                      }}
                    />
                  ))}
                </Box>
              )}
              renderInput={params => <TextField {...params} label={label} fullWidth required={required} />}
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.value}>
                    {chipLabel(option.value)}
                  </li>
                )
              }}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              filterSelectedOptions
              disableCloseOnSelect
              // Don't filter options based on input - let the backend handle search
              filterOptions={options => options}
              ListboxProps={{
                style: {
                  maxHeight: MENU_ITEM_HEIGHT * 4
                },
                onScroll: event => {
                  const listboxNode = event.currentTarget
                  if (listboxNode.scrollTop + listboxNode.clientHeight === listboxNode.scrollHeight) {
                    setPage?.(prev => prev + 1)
                  }
                }
              }}
            />
          )
        }}
      />
      {error && <FormHelperText style={{ color: '#d32f2f' }}>{error?.message}</FormHelperText>}
    </FormControl>
  )
}

export default CustomMultiSelectChip
